import { ScheduleItem } from '../types/schedule';

/**
 * Форматирует дату для календарей (формат: YYYYMMDDTHHmmssZ для UTC)
 */
function formatDateForCalendar(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Парсит дату и время из ScheduleItem и создает объекты Date
 */
function parseScheduleDateTime(item: ScheduleItem): { startDate: Date; endDate: Date } {
  // Парсим дату из формата DD.MM.YY или DD.MM.YYYY
  const [dayRaw, monthRaw, yearRaw] = (item.date ?? '').split('.');
  if (!dayRaw || !monthRaw || !yearRaw) {
    const invalid = new Date(NaN);
    return { startDate: invalid, endDate: invalid };
  }
  // Определяем формат: если год имеет 2 символа - старый формат (YY), если 4 - новый (YYYY)
  const fullYear = yearRaw.length === 2 ? '20' + yearRaw : yearRaw;
  const day = dayRaw.padStart(2, '0');
  const month = monthRaw.padStart(2, '0');
  
  // Парсим время из формата HH:MM
  const [hoursRaw, minutesRaw] = (item.time ?? '').split(':');
  if (!hoursRaw || !minutesRaw) {
    const invalid = new Date(NaN);
    return { startDate: invalid, endDate: invalid };
  }
  const hours = hoursRaw.padStart(2, '0');
  const minutes = minutesRaw.padStart(2, '0');
  
  // Создаем дату начала (в GMT+3, как указано в данных)
  const startDate = new Date(`${fullYear}-${month}-${day}T${hours}:${minutes}:00+03:00`);
  
  const parseDurationMs = (duration?: string): number => {
    if (!duration) return 0;
    const cleaned = duration.trim();
    if (!cleaned) return 0;
    const parts = cleaned.split(':').map(part => part.trim());
    let h = 0;
    let m = 0;
    let s = 0;
    if (parts.length === 3) {
      [h, m, s] = parts.map(part => Number(part));
    } else if (parts.length === 2) {
      [h, m] = parts.map(part => Number(part));
      s = 0;
    } else {
      return 0;
    }
    if ([h, m, s].some(value => Number.isNaN(value))) return 0;
    return Math.max(0, ((h * 60 + m) * 60 + s) * 1000);
  };

  const durationMs = parseDurationMs(item.Duration);
  const fallbackMs = 2 * 60 * 60 * 1000;
  const endDate = new Date(startDate.getTime() + (durationMs || fallbackMs));
  
  return { startDate, endDate };
}

/**
 * Формирует описание события из ScheduleItem
 */
function buildEventDescription(item: ScheduleItem): string {
  const descriptionParts = [
    item.stage && `Этап: ${item.stage}`,
    `Сессия: ${item.session}`,
    item.place && `Место: ${item.place}`,
    item.Commentator1 && `Комментатор: ${item.Commentator1}`,
    item.Commentator2 && `Комментатор: ${item.Commentator2}`,
    item.Optionally && `Важно: ${item.Optionally}`
  ].filter(Boolean);
  
  return descriptionParts.join('\n');
}

/**
 * Генерирует URL для добавления события в Google Calendar
 */
export function generateGoogleCalendarUrl(item: ScheduleItem): string {
  const { startDate, endDate } = parseScheduleDateTime(item);
  const startDateStr = formatDateForCalendar(startDate);
  const endDateStr = formatDateForCalendar(endDate);
  
  // Формируем название события
  const eventTitle = item.stage 
    ? `${item.championship} - ${item.stage}: ${item.session}`
    : `${item.championship}: ${item.session}`;
  const description = buildEventDescription(item);
  
  // Кодируем параметры для URL
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: eventTitle,
    dates: `${startDateStr}/${endDateStr}`,
    details: description,
    location: item.place || ''
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Экранирует специальные символы для формата iCalendar
 */
function escapeICS(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
}

/**
 * Генерирует файл iCalendar (.ics) для Яндекс Календаря и других календарей
 */
export function generateICalendarFile(item: ScheduleItem): string {
  const { startDate, endDate } = parseScheduleDateTime(item);
  const startDateStr = formatDateForCalendar(startDate);
  const endDateStr = formatDateForCalendar(endDate);
  const currentDateStr = formatDateForCalendar(new Date());
  
  // Формируем название события
  const eventTitle = item.stage 
    ? `${item.championship} - ${item.stage}: ${item.session}`
    : `${item.championship}: ${item.session}`;
  const description = buildEventDescription(item).replace(/\n/g, '\\n');
  const location = item.place || '';
  
  // Генерируем уникальный ID события
  const eventId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Формируем содержимое .ics файла
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BeOnEdge Schedule//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${eventId}@beonedge.com`,
    `DTSTAMP:${currentDateStr}`,
    `DTSTART:${startDateStr}`,
    `DTEND:${endDateStr}`,
    `SUMMARY:${escapeICS(eventTitle)}`,
    `DESCRIPTION:${escapeICS(description)}`,
    `LOCATION:${escapeICS(location)}`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
  
  return icsContent;
}

/**
 * Скачивает файл .ics для Яндекс Календаря
 */
export function downloadICalendarFile(item: ScheduleItem): void {
  const icsContent = generateICalendarFile(item);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  // Функция для безопасного форматирования имени файла
  const sanitizeFileName = (text: string): string => {
    return text.replace(/[^a-zA-Z0-9а-яА-Я]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  };
  
  const championship = sanitizeFileName(item.championship);
  const stage = item.stage ? sanitizeFileName(item.stage) : '';
  const session = sanitizeFileName(item.session);
  const date = item.date.replace(/\./g, '_');
  
  const link = document.createElement('a');
  link.href = url;
  link.download = stage 
    ? `${championship}-${stage}-${session}-${date}.ics`
    : `${championship}-${session}-${date}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

